import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import { useEffect, useRef } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { ActionComponentProps } from '../index';

type DialogIdCardConfirmationProps = ActionComponentProps & {
  actionRequestInProgress: boolean;
  onIdEntered: (idData: string, onError: (error: string) => void) => void;
  isSearchMatching: boolean;
  pendingSearchValue?: string;
};

type FormData = {
  idCard: string;
};

export const DialogIdCardConfirmation = ({
  mailpieces,
  actionStarted,
  actionRequestInProgress,
  onActionFinish,
  onIdEntered,
  isSearchMatching,
  pendingSearchValue,
}: DialogIdCardConfirmationProps) => {
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      idCard: isSearchMatching && pendingSearchValue ? pendingSearchValue : '',
    },
  });

  const textFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!actionStarted) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.code === '') {
        event.preventDefault();
        event.stopPropagation();

        const currentValue = getValues('idCard');
        setValue('idCard', currentValue + event.key, { shouldValidate: true });

        if (errors.idCard) {
          clearErrors('idCard');
        }

        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }
    };

    window.addEventListener('keypress', handleKeyPress, true);

    return () => {
      window.removeEventListener('keypress', handleKeyPress, true);
    };
  }, [actionStarted, setValue, getValues, clearErrors, errors.idCard]);

  const onSubmit: SubmitHandler<FormData> = (data) => {
    if (actionRequestInProgress) return;

    const handleError = (error: string) => {
      setError('idCard', { message: error });
    };

    onIdEntered(data.idCard.trim(), handleError);
  };

  const handleClose = () => {
    if (!actionRequestInProgress) {
      reset();
      onActionFinish();
    }
  };

  const title =
    mailpieces.length > 1 ? 'Complete pickup for selected items with ID card' : 'Complete pickup with ID card';
  const message =
    mailpieces.length > 1 ? (
      <>
        Please ask the recipient to scan their ID card to complete pickup for <strong>{mailpieces.length}</strong>{' '}
        items.
      </>
    ) : (
      <>Please ask the recipient to scan their ID card to complete pickup.</>
    );

  return (
    <Dialog fullWidth maxWidth="xs" open={actionStarted} onClose={handleClose} autoFocus={true}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {message}
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="idCard"
            control={control}
            rules={{
              required: 'ID card is required',
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                autoFocus
                label="ID Card Value"
                disabled={actionRequestInProgress}
                error={!!errors.idCard}
                helperText={errors.idCard?.message}
                inputRef={textFieldRef}
                onChange={(e) => {
                  field.onChange(e);
                  if (errors.idCard) {
                    clearErrors('idCard');
                  }
                }}
                slotProps={{
                  input: {
                    endAdornment: field.value && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear"
                          onClick={() => {
                            field.onChange('');
                            clearErrors('idCard');
                          }}
                          edge="end"
                          size="small"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={handleClose} disabled={actionRequestInProgress}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid || actionRequestInProgress}
        >
          Complete
        </Button>
      </DialogActions>
    </Dialog>
  );
};
